import { useState } from 'react'
import PropertyField from './PropertyField.jsx'
import Avatar from './Avatar.jsx'
import AssociationPicker from './AssociationPicker.jsx'
import { tsToDate } from '../lib/format.js'
import { StackColumns, Button, TagLabel, Segmented, Card, Breadcrumb } from '../ds/index.js'

/** Left "About" card: renders defs grouped by def.group, each via PropertyField. */
export function AboutCard({ defs = [], record = {}, users = [], onChange }) {
  const groups = []
  const byGroup = {}
  defs.forEach((d) => {
    if (!byGroup[d.group]) { byGroup[d.group] = []; groups.push(d.group) }
    byGroup[d.group].push(d)
  })
  const valueOf = (d) => (d.isCore ? record[d.key] : (record.custom || {})[d.key])
  return (
    <Card>
      {groups.map((g) => (
        <div key={g} style={{ marginBottom: 18 }}>
          <div className="eyebrow" style={{ marginBottom: 10 }}>
            <span className="tag-label" style={{ color: 'var(--color-gold)' }}>{g}</span>
          </div>
          {byGroup[g].map((d) => (
            <PropertyField key={d.key} def={d} value={valueOf(d)} users={users}
              onChange={(v) => onChange && onChange(d.key, v, d.isCore)} />
          ))}
        </div>
      ))}
    </Card>
  )
}

/** Center timeline: newest-first activity rows + optional composer node. */
export function RecordTimeline({ activities = [], usersById = {}, composer = null }) {
  // Enforce newest-first here so the guarantee holds regardless of caller ordering.
  const sorted = [...activities].sort(
    (a, b) => (tsToDate(b.createdAt)?.getTime() || 0) - (tsToDate(a.createdAt)?.getTime() || 0),
  )
  return (
    <div>
      {composer}
      <div style={{ marginTop: composer ? 16 : 0 }}>
        {sorted.length === 0 && (
          <div style={{ color: 'var(--color-muted)', fontSize: 13, padding: '12px 0' }}>No activity yet.</div>
        )}
        {sorted.map((a) => {
          const who = usersById[a.actorUid]
          const when = tsToDate(a.createdAt)
          const display = a.type === 'form-submission' && Array.isArray(a.meta?.display) ? a.meta.display : null
          return (
            <div key={a.id} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--surface-row)' }}>
              {who && <Avatar user={who} size={24} />}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, color: 'var(--color-white)' }}>{a.body}</div>
                {display && (
                  <div style={{ margin: '6px 0 2px', padding: '8px 10px', background: 'var(--color-bg-dark)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-divider-dark)' }}>
                    {display.map((row, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, fontSize: 12, padding: '2px 0' }}>
                        <span style={{ color: 'var(--color-muted)', flexShrink: 0 }}>{row.label}</span>
                        <span style={{ color: 'var(--color-white)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{row.value}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="font-mono" style={{ fontSize: 10, color: 'var(--color-muted)', marginTop: 2 }}>
                  {a.type}{when ? ` · ${when.toLocaleDateString()}` : ''}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Right column: one collapsible group per associated object type. */
export function AssociationsColumn({ groups = [] }) {
  const [openPicker, setOpenPicker] = useState(null) // group title whose picker is open
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {groups.map((grp) => {
        const canAdd = Array.isArray(grp.candidates)
        return (
          <Card key={grp.title}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <span className="tag-label" style={{ color: 'var(--color-muted)' }}>{grp.title}</span>
              <span className="font-mono" style={{ marginLeft: 8, fontSize: 10, color: 'var(--color-muted)' }}>{grp.items.length}</span>
              <div style={{ flex: 1 }} />
              {canAdd && (
                <div className="cn-popover-wrap">
                  <Button variant="ghost" size="sm" style={{ padding: '2px 8px', fontSize: 11 }}
                    onClick={() => setOpenPicker(openPicker === grp.title ? null : grp.title)}>+</Button>
                  <AssociationPicker
                    open={openPicker === grp.title}
                    candidates={grp.candidates}
                    existingIds={grp.items.map((it) => it.id)}
                    labelOf={grp.labelOf || ((r) => r.name || r.id)}
                    onPick={(r) => { setOpenPicker(null); grp.onLink && grp.onLink(r) }}
                    onClose={() => setOpenPicker(null)}
                  />
                </div>
              )}
            </div>
            {grp.items.map((it) => (
              <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button type="button" onClick={it.onOpen}
                  style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 4px', borderRadius: 'var(--radius-sm)' }}>
                  <div style={{ fontSize: 13, color: 'var(--color-white)' }}>{it.label}</div>
                  {it.sub && <div style={{ fontSize: 11, color: 'var(--color-muted)' }}>{it.sub}</div>}
                </button>
                {grp.onUnlink && (
                  <button type="button" title="Unlink" onClick={() => grp.onUnlink(it)}
                    style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 13, padding: '0 4px' }}>×</button>
                )}
              </div>
            ))}
            {grp.items.length === 0 && <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>None yet.</div>}
          </Card>
        )
      })}
    </div>
  )
}

/** Internal center sub-tab: keeps both overview and activity mounted (SSR-safe), toggles visibility. */
function CenterTabs({ overview, activity }) {
  const [tab, setTab] = useState('overview')
  return (
    <>
      <div className="cn-record-center-tabs" style={{ marginBottom: 12 }}>
        <Segmented size="sm" value={tab} onChange={setTab}
          options={[{ key: 'overview', label: 'Overview' }, { key: 'activity', label: 'Activity' }]} />
      </div>
      <div style={{ display: tab === 'overview' ? 'block' : 'none' }}>{overview}</div>
      <div style={{ display: tab === 'activity' ? 'block' : 'none' }}>{activity}</div>
    </>
  )
}

// objectChip is a singular/board badge; the breadcrumb parent should read as the
// object's INDEX. Map the singular CRM chips to their plural index; engagement
// board chips ("Speaking" / "PR & Media") and any future type pass through.
const INDEX_LABELS = { Contact: 'Contacts', Company: 'Companies', Deal: 'Deals' }

/** Three-column record shell. Zones are pre-rendered nodes supplied by wrappers. */
export default function RecordPage({ objectChip, title, meta, onBack, actions, left, center, right }) {
  const parentLabel = INDEX_LABELS[objectChip] || objectChip
  // `meta` is the optional identity line under the title (register item 15): the
  // few facts you need before reading anything else. Callers pass already-formatted
  // strings; falsy entries are dropped so an absent date or fee leaves no stray dot.
  const metaParts = (Array.isArray(meta) ? meta : [meta]).filter(Boolean)
  return (
    <div style={{ padding: '18px 24px' }}>
      {/* canonical breadcrumb: Index › Record — parent links to the object's index;
          traversal history stays the browser Back button's job */}
      <Breadcrumb items={[{ label: parentLabel, onClick: onBack }, { label: title }]} />
      {/* title row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: metaParts.length ? 6 : 18 }}>
        <TagLabel>{objectChip}</TagLabel>
        <h1 className="cn-page-title">{title}</h1>
        <div style={{ flex: 1 }} />
        {actions}
      </div>
      {metaParts.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0 8px', marginBottom: 18, fontSize: 13, color: 'var(--color-muted)' }}>
          {metaParts.map((part, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
              {i > 0 && <span aria-hidden="true">·</span>}
              <span>{part}</span>
            </span>
          ))}
        </div>
      )}
      {/* three columns, kit StackColumns: desktop 3-col, tablet right-tuck, phone Overview/Activity/Related tabs */}
      <StackColumns
        left={left}
        center={<CenterTabs overview={center?.overview} activity={center?.activity} />}
        right={right}
      />
    </div>
  )
}
